import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Collapse,
	Grid2,
	IconButton,
	Paper,
	Typography,
} from "@mui/material";
import React, { type MouseEventHandler } from "react";

type AlertProps = {
	show: boolean;
	variant: "success" | "danger";
	dismissible?: boolean;
	onClose?: MouseEventHandler;
	children: React.ReactNode;
};

const Alert = ({
	show,
	variant,
	dismissible,
	onClose,
	children,
}: AlertProps) => {
	return (
		<Collapse in={show}>
			<Paper
				sx={{
					padding: (theme) => theme.spacing(1, 2),
					backgroundColor: variant === "success" ? "#d4edda" : "#f8d7da",
					color: variant === "success" ? "#155724" : "#721c24",
					border:
						variant === "success" ? "1px solid #c3e6cb" : "1px solid #f5c6cb",
				}}
				elevation={0}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ padding: (theme) => theme.spacing(1) }}>{children}</Box>
					{dismissible && onClose && (
						<IconButton
							size="small"
							sx={{ padding: (theme) => theme.spacing(0.5) }}
							onClick={onClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					)}
				</Box>
			</Paper>
		</Collapse>
	);
};

type GenericPanelProps = {
	title: string;
	description?: string;
	showPromptSuccess?: boolean;
	showPromptError?: boolean;
	onDismissSuccess?: MouseEventHandler;
	onDismissError?: MouseEventHandler;
	successMessage?: string;
	error?: any;
	children: any;
};

const GenericPanel = ({
	title,
	description = "",
	showPromptSuccess = false,
	showPromptError = false,
	onDismissSuccess = () => null,
	onDismissError = () => null,
	successMessage,
	error,
	children,
}: GenericPanelProps) => {
	return (
		<Box sx={{ padding: (theme) => theme.spacing(2) }}>
			<Box sx={{ marginBottom: (theme) => theme.spacing(2) }}>
				<Alert
					show={showPromptError}
					variant="danger"
					dismissible={true}
					onClose={onDismissError}
				>
					<Typography variant="subtitle1" component="strong">
						There was an error
					</Typography>
					<Typography variant="body2" component="p">
						{error === undefined
							? "There was an error"
							: error?.internalError.message === undefined
								? error?.internalError
								: error?.internalError?.message}
					</Typography>
				</Alert>
				<Alert
					show={showPromptSuccess}
					variant="success"
					onClose={onDismissSuccess}
					dismissible={true}
				>
					<Typography variant="body2" component="p">
						{successMessage || "Operation was success"}
					</Typography>
				</Alert>
			</Box>
			<Typography
				variant="h5"
				component="h3"
				sx={{
					marginBottom: (theme) => theme.spacing(2),
					"& small": {
						marginLeft: (theme) => theme.spacing(1),
						color: (theme) => theme.palette.text.secondary,
					},
				}}
			>
				{title}
				<Typography variant="body2" component="small">
					{description}
				</Typography>
			</Typography>
			{children}
		</Box>
	);
};

type MasterDetailPanelProps = {
	title: string;
	onSave: MouseEventHandler;
	onCancel: MouseEventHandler;
	onDelete: MouseEventHandler;
	saveStatus: boolean;
	deleteStatus: boolean;
	showSave: boolean;
	showDelete: boolean;
	children?: React.ReactNode;
};

const MasterDetailPanel = ({
	title,
	onSave,
	onCancel,
	onDelete,
	saveStatus,
	deleteStatus,
	showSave = true,
	showDelete,
	children,
}: MasterDetailPanelProps) => {
	return (
		<Box sx={{ marginBottom: (theme) => theme.spacing(2) }}>
			<Card variant="outlined">
				<CardContent>
					<Typography gutterBottom variant="h5" component="h2">
						{title || ""}
					</Typography>
					{children}
				</CardContent>
				<CardActions>
					<Box display="flex" justifyContent="flex-end" width="100%">
						<Button
							variant="contained"
							sx={{
								marginLeft: (theme) => theme.spacing(1),
								backgroundColor: (theme) => theme.palette.error.main,
								color: (theme) => theme.palette.error.contrastText,
								"&:hover": {
									backgroundColor: (theme) => theme.palette.error.dark,
								},
							}}
							onClick={onCancel}
							disabled={!saveStatus}
						>
							Cancel
						</Button>
						{(showSave === undefined || showSave) && (
							<Button
								variant="contained"
								color="primary"
								sx={{
									marginLeft: (theme) => theme.spacing(1),
								}}
								onClick={onSave}
								disabled={!saveStatus}
							>
								Save
							</Button>
						)}
						{showDelete && (
							<Button
								variant="contained"
								color="primary"
								sx={{
									marginLeft: (theme) => theme.spacing(1),
								}}
								onClick={onDelete}
								disabled={!deleteStatus}
							>
								Delete
							</Button>
						)}
					</Box>
				</CardActions>
			</Card>
		</Box>
	);
};

type ExtraButton = {
	name: string;
	onClick: MouseEventHandler;
};

type FilterPanelProps = {
	onClickExport?: Function;
	onClickReset: Function;
	OnClickGo: Function;
	showButtons: boolean;
	showExport: boolean;
	extraButtons?: ExtraButton[];
	children?: React.ReactNode;
};

const FilterPanel = (props: FilterPanelProps) => {
	const {
		onClickExport,
		onClickReset,
		OnClickGo,
		showButtons,
		showExport,
		extraButtons = [],
		children,
	} = props;

	const handleExport = () => {
		if (onClickExport) onClickExport();
	};

	const handleReset = () => {
		if (onClickReset) onClickReset();
	};

	const handleGo = () => {
		if (OnClickGo) OnClickGo();
	};

	return (
		<Box
			sx={{
				marginBottom: (theme) => theme.spacing(2),
			}}
		>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12 }}>
					{children}

					{showButtons && (
						<Box
							sx={{ display: "flex", marginTop: (theme) => theme.spacing(2) }}
						>
							<Button
								variant="contained"
								color="primary"
								onClick={handleGo}
								sx={{
									marginLeft: (theme) => theme.spacing(1),
								}}
							>
								Go
							</Button>
							<Button
								variant="contained"
								sx={{
									marginLeft: (theme) => theme.spacing(1),
									backgroundColor: (theme) => theme.palette.error.main,
									color: (theme) => theme.palette.error.contrastText,
									"&:hover": {
										backgroundColor: (theme) => theme.palette.error.dark,
									},
								}}
								onClick={handleReset}
								size="small"
							>
								Reset
							</Button>

							{extraButtons.map((button, index) => (
								<Button
									key={index}
									variant="contained"
									color="primary"
									size="small"
									onClick={button.onClick}
									sx={{
										marginLeft: (theme) => theme.spacing(1),
									}}
								>
									{button.name}
								</Button>
							))}

							{showExport && (
								<Button
									variant="contained"
									sx={{
										marginLeft: (theme) => theme.spacing(1),
										backgroundColor: "#28a745",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#218838",
										},
									}}
									onClick={handleExport}
									startIcon={<GetAppIcon />}
								>
									Excel
								</Button>
							)}
						</Box>
					)}
				</Grid2>
			</Grid2>
		</Box>
	);
};

const LongFilterPanel = (props: FilterPanelProps) => {
	const {
		onClickExport,
		onClickReset,
		OnClickGo,
		showButtons,
		showExport,
		extraButtons = [],
		children,
	} = props;

	const handleExport = () => {
		if (onClickExport) onClickExport();
	};

	const handleReset = () => {
		if (onClickReset) onClickReset();
	};

	const handleGo = () => {
		if (OnClickGo) OnClickGo();
	};

	return (
		<Box sx={{ display: "flex", marginTop: (theme) => theme.spacing(2) }}>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12 }}>
					{children}

					{showButtons && (
						<Box
							sx={{ display: "flex", marginTop: (theme) => theme.spacing(2) }}
						>
							<Button
								variant="contained"
								color="primary"
								onClick={handleGo}
								sx={{
									marginLeft: (theme) => theme.spacing(1),
								}}
							>
								Go
							</Button>
							<Button
								variant="contained"
								sx={{
									marginLeft: (theme) => theme.spacing(1),
									backgroundColor: (theme) => theme.palette.error.main,
									color: (theme) => theme.palette.error.contrastText,
									"&:hover": {
										backgroundColor: (theme) => theme.palette.error.dark,
									},
								}}
								onClick={handleReset}
								size="small"
							>
								Reset
							</Button>
							{showExport && (
								<Button
									variant="contained"
									sx={{
										marginLeft: (theme) => theme.spacing(1),
										backgroundColor: "#28a745",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#218838",
										},
									}}
									onClick={handleExport}
									startIcon={<GetAppIcon />}
								>
									Excel
								</Button>
							)}
							{extraButtons.map((button, index) => (
								<Button
									key={index}
									variant="contained"
									color="primary"
									size="small"
									onClick={button.onClick}
									sx={{
										marginLeft: (theme) => theme.spacing(1),
									}}
								>
									{button.name}
								</Button>
							))}
						</Box>
					)}
				</Grid2>
			</Grid2>
		</Box>
	);
};

export { FilterPanel, GenericPanel, LongFilterPanel, MasterDetailPanel };
